import React, { useState, useEffect } from "react";
import { Modal, Button, Checkbox, message } from "antd";
import { sortableContainer, sortableElement } from "react-sortable-hoc";
import { MenuOutlined } from "@ant-design/icons";
import arrayMove from "array-move";
import "./favorite-menu-modal.less";
import { lib } from "../index";

// 递归获取所有菜单项（扁平化），只返回叶子节点（最后一级）
const flattenMenuList = (menuList, parentUrl = "", parentTitles = [], level = 1) => {
    let result = [];
    menuList.forEach(item => {
        const currentTitle = item.title;
        // 构建完整的 URL 路径
        let currentUrl = "";
        if (item.url) {
            // 如果是绝对路径（http:// 或 https://），直接使用
            if (item.url.indexOf("http://") > -1 || item.url.indexOf("https://") > -1) {
                currentUrl = item.url;
            } else {
                // 否则拼接父级 URL
                currentUrl = parentUrl ? parentUrl + item.url : item.url;
            }
        } else {
            currentUrl = parentUrl;
        }

        // 如果有子菜单，递归处理
        if (item.list && item.list.length > 0) {
            // 递归处理子菜单，不添加当前项（因为它不是叶子节点）
            const childItems = flattenMenuList(item.list, currentUrl, [...parentTitles, currentTitle], level + 1);
            result = result.concat(childItems);
        } else {
            // 没有子菜单的项目（叶子节点），才添加到结果中
            result.push({
                id: item.id,
                title: item.title,
                url: currentUrl,
                icon: item.icon,
                parentTitles: parentTitles, // 所有父级标题的数组 [一级, 二级]
                level: level, // 记录层级 (1=一级, 2=二级, 3=三级)
            });
        }
    });
    return result;
};

// 验证菜单项是否在菜单树中有效存在（验证完整路径）
const isMenuItemValid = (menuItem, menuList) => {
    // 递归查找菜单项
    const findInMenu = (items, targetId, parentUrl = "") => {
        for (const item of items) {
            // 构建完整的 URL 路径
            let currentUrl = "";
            if (item.url) {
                // 如果是绝对路径（http:// 或 https://），直接使用
                if (item.url.indexOf("http://") > -1 || item.url.indexOf("https://") > -1) {
                    currentUrl = item.url;
                } else {
                    // 否则拼接父级 URL
                    currentUrl = parentUrl ? parentUrl + item.url : item.url;
                }
            } else {
                currentUrl = parentUrl;
            }

            // 如果找到匹配的 ID
            if (item.id === targetId) {
                // 验证 URL 是否匹配（确保路径正确）
                if (item.list && item.list.length > 0) {
                    // 如果有子菜单，说明不是叶子节点，无效
                    return false;
                }
                // 验证 URL 是否匹配
                return currentUrl === menuItem.url;
            }

            // 递归查找子菜单
            if (item.list && item.list.length > 0) {
                const found = findInMenu(item.list, targetId, currentUrl);
                if (found) return true;
            }
        }
        return false;
    };

    return findInMenu(menuList, menuItem.id);
};

function FavoriteMenuModal({ visible, onCancel, menuList, onSave }) {
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [favoriteList, setFavoriteList] = useState([]);
    const [allMenuItems, setAllMenuItems] = useState([]);

    useEffect(() => {
        if (menuList && menuList.length > 0) {
            const flattened = flattenMenuList(menuList);
            setAllMenuItems(flattened);
        }
    }, [menuList]);

    // 从接口加载常用菜单
    useEffect(() => {
        if (visible && allMenuItems.length > 0 && menuList && menuList.length > 0) {
            lib.request({
                url: "/ucenter-account/frequentMenu/list",
                data: {
                    systemCode: lib.config.systemCode,
                },
                success: data => {
                    // 接口返回格式：[{ code, icon, id, systemCode, title, url }]
                    // 需要转换为内部格式并验证菜单是否有效
                    const favoriteItems = (data || [])
                        .map(item => {
                            // 在 allMenuItems 中查找对应的菜单项
                            const menuItem = allMenuItems.find(m => m.id === item.id);
                            if (menuItem && isMenuItemValid(menuItem, menuList)) {
                                return menuItem;
                            }
                            return null;
                        })
                        .filter(item => item !== null);

                    setFavoriteList(favoriteItems);
                    setSelectedIds(new Set(favoriteItems.map(item => item.id)));
                },
                fail: () => {
                    console.error("加载常用菜单失败");
                },
            });
        }
    }, [visible, allMenuItems, menuList]);

    const handleCheckboxChange = (item, checked) => {
        const newSelectedIds = new Set(selectedIds);
        if (checked) {
            if (favoriteList.length >= 10) {
                message.warning("最多可选择10项");
                return;
            }
            newSelectedIds.add(item.id);
            setFavoriteList([...favoriteList, item]);
        } else {
            newSelectedIds.delete(item.id);
            setFavoriteList(favoriteList.filter(f => f.id !== item.id));
        }
        setSelectedIds(newSelectedIds);
    };

    const handleSortEnd = ({ oldIndex, newIndex }) => {
        setFavoriteList(arrayMove(favoriteList, oldIndex, newIndex));
    };

    const handleSave = () => {
        let params = {
            id: lib.getParam("userId"),
            permissionIds: (favoriteList || []).map(item => item.id),
            systemCode: lib.config.systemCode,
        };
        lib.request({
            url: "/ucenter-account/frequentMenu/save",
            data: params,
            success: () => {
                message.success("保存成功");
                // 通过回调通知父组件更新
                onSave && onSave();
                onCancel();
            },
            fail: () => {
                message.error("保存菜单数据失败");
            },
        });
    };

    const handleClear = () => {
        setFavoriteList([]);
        setSelectedIds(new Set());
    };

    // 按层级结构组织菜单
    const menuStructure = {};
    allMenuItems.forEach(item => {
        const level1 = item.parentTitles[0] || item.title; // 一级菜单
        const level2 = item.parentTitles[1]; // 二级菜单（可能不存在）

        if (!menuStructure[level1]) {
            menuStructure[level1] = {};
        }

        if (item.level === 1) {
            // 一级菜单的叶子节点（有 URL 且没有子菜单）
            if (!menuStructure[level1]["_items"]) {
                menuStructure[level1]["_items"] = [];
            }
            menuStructure[level1]["_items"].push(item);
        } else if (item.level === 2) {
            // 二级菜单的子项
            if (!menuStructure[level1]["_items"]) {
                menuStructure[level1]["_items"] = [];
            }
            menuStructure[level1]["_items"].push(item);
        } else if (item.level === 3) {
            // 三级菜单的子项
            if (!menuStructure[level1][level2]) {
                menuStructure[level1][level2] = [];
            }
            menuStructure[level1][level2].push(item);
        }
    });

    // 处理取消选中
    const handleRemoveItem = item => {
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(item.id);
        setSelectedIds(newSelectedIds);
        setFavoriteList(favoriteList.filter(f => f.id !== item.id));
    };

    // 可排序的项
    const SortableItem = sortableElement(({ item }) => (
        <div className="favorite-item">
            {/* <Checkbox
                checked={true}
                onChange={() => handleRemoveItem(item)}
            /> */}
            <MenuOutlined className="drag-icon" />
            <span className="item-title">{item.title}</span>
        </div>
    ));

    // 可排序的列表
    const SortableList = sortableContainer(({ items }) => (
        <div className="favorite-list">
            {items.map((item, index) => (
                <SortableItem key={item.id} index={index} item={item} />
            ))}
        </div>
    ));

    return (
        <Modal
            title="常用菜单管理"
            open={visible}
            onCancel={onCancel}
            width={900}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    保存
                </Button>,
            ]}>
            <div className="favorite-menu-modal">
                <div className="menu-select-area">
                    {Object.keys(menuStructure).map(level1Title => (
                        <div key={level1Title} className="menu-category">
                            <div className="category-title">{level1Title}</div>

                            {/* 二级菜单的直接子项 */}
                            {menuStructure[level1Title]["_items"] &&
                                menuStructure[level1Title]["_items"].length > 0 && (
                                    <div className="menu-items">
                                        {menuStructure[level1Title]["_items"].map(item => (
                                            <Checkbox
                                                key={item.id}
                                                checked={selectedIds.has(item.id)}
                                                onChange={e => handleCheckboxChange(item, e.target.checked)}>
                                                {item.title}
                                            </Checkbox>
                                        ))}
                                    </div>
                                )}

                            {/* 三级菜单 */}
                            {Object.keys(menuStructure[level1Title])
                                .filter(key => key !== "_items")
                                .map(level2Title => (
                                    <div key={level2Title} className="menu-subcategory">
                                        <div className="subcategory-title">{level2Title}</div>
                                        <div className="menu-items menu-items-indent">
                                            {menuStructure[level1Title][level2Title].map(item => (
                                                <Checkbox
                                                    key={item.id}
                                                    checked={selectedIds.has(item.id)}
                                                    onChange={e => handleCheckboxChange(item, e.target.checked)}>
                                                    {item.title}
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ))}
                </div>
                <div className="menu-added-area">
                    <div className="added-header">
                        <span>已添加{favoriteList.length}项</span>
                        <Button type="link" onClick={handleClear}>
                            清空
                        </Button>
                    </div>
                    <div className="added-hint">最多可选10项，支持拖拽排序</div>
                    <SortableList items={favoriteList} onSortEnd={handleSortEnd} helperClass="favorite-item-dragging" />
                </div>
            </div>
        </Modal>
    );
}

export default FavoriteMenuModal;
